import * as _ from 'lodash'
import {IBusinessObject} from '../PA.Core/PA.Core.IF.BO';
import {IMessageProvider, IProgressProvider, MessageType} from '../PA.Core/PA.Core.IF.Basic';
import {PAdbContext} from '../PA.Core/PAdbContext';
import {IdentityProvider} from '../PA.Core/Basic/Identity'
import {MessageProvider} from '../PA.Core/Basic/Messages'
import {ProgressProvider} from '../PA.Core/Basic/Progress'
import {
    WB_GLTransactionItemRow,
    WB_GLTransactionRow, WB_PeriodRow, WB_PeriodTypeRow,
} from "../PA.Core/PAdbSchemaRows";
import {IwbGLTransaction, IwbTransaction} from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_GLTransaction, esWB_GLTransaction_Item, esWB_Period, esWB_Period_Type
} from "../PA.Core/PAdbSchema";
import {ESDataStore} from "../PA.Core/lib/ESDataStore";
import {ESRecord} from "../PA.Core/lib/ESRecord";
import {CreateOptions, ESRecordResolver} from "../PA.Core/lib/ESRecordResolver"
import {ASync} from "../PA.Core/Basic/ASync"
import {GLTransactionBOValidations} from "./GLTransactionBO.Validations"
import {PAEnvironment} from "../PA.Core/PAEnvironment";


export class GLTransactionBO implements IBusinessObject {

    //<editor-fold desc = "Properties and Constructor">

    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_GLTransaction: WB_GLTransactionRow;

    constructor(options?: any) {

        this.CreateOptions = options || {};

        if ('dbContext' in this.CreateOptions)
            this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({id: 0, name: 'New Transaction'});

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider('Loading', this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_GLTransaction = undefined;
    }

    //</editor-fold>

    get WB_GLTransaction(): WB_GLTransactionRow {
        return this.drWB_GLTransaction;
    }


    //<editor-fold desc = "IBusinessObject operations">

    LoadNew(options?: object): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Transaction");

        let createOpts = _.defaultsDeep({}, self.CreateOptions, options)

        let internalOnComplete = new Promise<IBusinessObject>((resolve, error) => {

            self.LoadOptions = options || {};
            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else
                self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                var sError = "Unable to determine dbContext for new Transaction.";
                self.LoadProgressProvider.exception(sError);
                error(sError);
                return;
            }

            // Create record using dbContext
            this.dbContext.createRecordWithKey(esWB_GLTransaction)
                .then((newTransactionRow: WB_GLTransactionRow) => {
                    newTransactionRow.setDefaults()
                    self.drWB_GLTransaction = newTransactionRow;
                    self.dbContext.add(esWB_GLTransaction, newTransactionRow)
                    self.Identity.setAll(self.WB_GLTransaction.cif.WB_GLTXN_ID, self.WB_GLTransaction.cif.TXN_ID, self.WB_GLTransaction.cif.TXN_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_GLTransaction, 'WB_GLTXN_ID', 'TXN_ID', 'TXN_NAME')

                    //debugger
                    self.LoadProgressProvider.finish();
                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    resolve(self);

                })
                .catch((err) => {
                    self.LoadProgressProvider.exception(err)
                    error(err)
                })

        });

        self._OnLoadComplete = internalOnComplete

        return new Promise<IBusinessObject>((resolve, error) => {
            internalOnComplete
                .then((BO) => {
                    return ASync.resolve()
                }).then((ignored) => {
                self._bIsLoaded = true;
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.finish()
                resolve(self)
            }).catch((err) => {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
            })
        })
    }

    Load(anId: number | number, options?: object): Promise<IBusinessObject> {

        const self = this;

        let recordResolver: ESRecordResolver
        let recordResolverLoadPromise: Promise<void>

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, error) => {

            if (self.bIsLoaded) {
                self.LoadProgressProvider.exception("Transaction already loaded.")
                resolve(self); // "Transaction already loaded.";
                return;
            }

            self.LoadOptions = options || {};
            _.defaults(self.LoadOptions, {})


            if ('dbContext' in self.LoadOptions)
                self.dbContext = self.LoadOptions.dbContext;
            else if (_.isEmpty(self.dbContext)) {
                self.dbContext = new PAdbContext({});
            }

            if (_.isEmpty(self.dbContext)) {
                error("Unable to determine dbContext for Transaction.")
                return;
            }

            if ('RecordResolver' in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver
                recordResolverLoadPromise = ASync.resolve()
            } else {
                recordResolver = GLTransactionBO.createRecordResolver({dbContext: self.dbContext})
                recordResolverLoadPromise = GLTransactionBO.setupRecordResolver(recordResolver, [anId])
            }


            recordResolverLoadPromise
                .then(function () {
                    let aTransactionRow: any = recordResolver.get(esWB_GLTransaction, anId)

                    if (_.isEmpty(aTransactionRow)) {
                        error("Transaction not Found");
                        return;
                    }

                    self.drWB_GLTransaction = aTransactionRow;
                    self.Identity.setAll(self.WB_GLTransaction.cif.WB_GLTXN_ID, self.WB_GLTransaction.cif.TXN_ID, self.WB_GLTransaction.cif.TXN_NAME);
                    self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                    self.Identity.listenToRecordUpdates(self.WB_GLTransaction, 'WB_GLTXN_ID', 'TXN_ID', 'TXN_NAME')
                    self._bIsLoaded = true
                    self._OnLoadComplete = undefined
                    self.LoadProgressProvider.finish()
                    resolve(self)
                }).catch(function (err: any) {
                self._OnLoadComplete = undefined
                self.LoadProgressProvider.exception(err)
                error(err)
                return;
            });

        });

        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }


    Validate(options?: any): Promise<IBusinessObject> {

        let self = this;
        this.ValidateMessageProvider.clearMessages();
        this.ValidateProgressProvider.start("Validating " + self.WB_GLTransaction.cif.TXN_NAME + "(" + self.WB_GLTransaction.cif.TXN_ID + ")");
        return new Promise<IBusinessObject>((resolve, reject) => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            if (self.dbContext == undefined) {
                var sError = "Unable to determine dbContext for to Validate Transaction.";
                self.ValidateProgressProvider.exception(sError);
                reject(sError);
            }

            if ('bSkipValidation' in self.ValidateOptions) {
                self._bIsValidated = true;
                self.ValidateProgressProvider.finish();
                resolve(self);
                return
            }

            GLTransactionBOValidations.ValidateEmptyFields(self)
                .then((ignore) => {
                    //return  next validation
                    // ()then hanadler

                    if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                        self._bIsValidated = false;
                    } else {
                        self._bIsValidated = true;
                    }
                    self.ValidateProgressProvider.finish();
                    resolve(self);

                })
                .catch((err) => {
                    self._bIsValidated = false;
                    self.ValidateProgressProvider.exception(err);
                    resolve(self);

                })
        });
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }


    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options)
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options)
    }

    SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {

        var self = this;
        var wbTxn = <IwbGLTransaction>self.WB_GLTransaction;
        this.SaveProgressProvider.start("Saving " + wbTxn.TXN_NAME + "(" + wbTxn.TXN_ID + ")");

        return new Promise<IBusinessObject>(async (resolve, reject) => {
            // Doesn't need to do anything yet
            // All the records are in the store, since they were added through find or createNewRecord.
            // -- We could search the tree if the there is any record where store isn't set.

            if (self.dbContext == undefined) {
                self._bIsSaved = false;
                let sError = "Unable to determine dbContext to Save GL Transaction.";
                self.SaveProgressProvider.exception(sError);
                reject(sError);
                return
            }

            self.SaveOptions = options || {};

            let saveFunction: (esName: string, saveRecords: ESRecord[] | ESRecord, options?: any) => Promise<ESDataStore>
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext)
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext)
            }

            // Add code to do individual BO save for dbContextRows....
            try {
                await self.Validate(self.SaveOptions)

                if (!self.bIsValidated) {
                    self._bIsSaved = false;
                    self.SaveProgressProvider.finish();
                    resolve(self)
                    return
                } else {
                    if ((self.drWB_GLTransaction.cif.WB_PERIOD_ID_FY === 0 || _.isUndefined(self.drWB_GLTransaction.cif.WB_PERIOD_ID_FY)) && self.drWB_GLTransaction.cif.POSTING_DATE) {
                        let yType: WB_PeriodTypeRow[] = await self.dbContext.findAll(esWB_Period_Type, {
                            where: {
                                DELETED_IND: {neq: '1'},
                                WB_MDSET_ID: self.drWB_GLTransaction.cif.WB_MDSET_ID,
                                PERIOD_TYPE_ID: 'Year'
                            }
                        })
                        if (yType.length) {
                            let yPeriod: WB_PeriodRow[] = await self.dbContext.findAll(esWB_Period, {
                                where: {
                                    DELETED_IND: {neq: '1'},
                                    WB_MDSET_ID: self.drWB_GLTransaction.cif.WB_MDSET_ID,
                                    PERIOD_START_DT: {lte: self.drWB_GLTransaction.cif.POSTING_DATE},
                                    PERIOD_END_DT: {gte: self.drWB_GLTransaction.cif.POSTING_DATE},
                                    WB_PERIODM_ID: yType[0].cif.WB_PERIODM_ID
                                }
                            })

                            if (yPeriod.length)
                                self.drWB_GLTransaction.cif.WB_PERIOD_ID_FY = yPeriod[0].cif.WB_PERIOD_ID
                        }

                    }


                    let tasks: Promise<any>[] = []
                    tasks.push(saveFunction(esWB_GLTransaction, self.drWB_GLTransaction, {}))
                    tasks.push(saveFunction(esWB_GLTransaction_Item, self.drWB_GLTransaction.WB_GLTransactionItemRows))
                    await Promise.all(tasks)


                    if (bBatch) {
                        self.dbContext.addBatchCompleteListener(() => {
                            self._bIsSaved = true;
                        })
                    } else {
                        self._bIsSaved = true;
                    }
                    self.SaveProgressProvider.finish();
                    resolve(self)
                    return
                }
            } catch (err) {
                self.SaveProgressProvider.exception(err);
                reject(self)
                return
            }

        });
    }


    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

//Delete
    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options)
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options)
    }

    private DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        var self = this;
        var wbTransaction = <IwbGLTransaction>self.drWB_GLTransaction;
        this.DeleteMessageProvider.clearMessages();
        this.DeleteProgressProvider.start("Deleting " + wbTransaction.TXN_NAME + "(" + wbTransaction.WB_GLTXN_ID + ")");
        return new Promise<IBusinessObject>((resolve, error) => {

            // 1. Validate if we can delete the Transaction

            // 2. If Valid, mark all the records for deletion.
            self.drWB_GLTransaction.markDeleted()

            _.forEach(self.drWB_GLTransaction.WB_GLTransactionItemRows, row => {
                row.markDeleted()
            })

            if (bSaveNow) {
                self.SaveUsing(false)
                    .then((ignore) => {
                        self._bIsDeleted = true;
                        self.DeleteProgressProvider.finish();
                        resolve(self);

                    })
                    .catch((err) => {
                        this.DeleteProgressProvider.exception(err)
                        resolve(self)
                    })
            } else {
                self._bIsDeleted = true;
                self.DeleteProgressProvider.finish();
                resolve(self);
            }
        });
    }


    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //------------TRANSACTION ITEMS---------------------------------------------------
//<editor-fold desc = "Transaction Items">
    addTxnItem(): Promise<IBusinessObject> {
        let self = this
        return new Promise<IBusinessObject>((resolve, error) => {
            self.dbContext.createRecordWithKey(esWB_GLTransaction_Item)
                .then((aNewRow: WB_GLTransactionItemRow) => {
                    aNewRow.setDefaults()
                    aNewRow.cif.WB_TXN_ID = self.drWB_GLTransaction.cif.WB_GLTXN_ID
                    aNewRow.WB_GLTransactionRow = self.drWB_GLTransaction
                    self.dbContext.add(esWB_GLTransaction_Item, aNewRow)
                    resolve(self)
                })
                .catch((err) => {
                    error(err)
                })
        })
    }

    removeTxnItem(aTxnItemId: number): Promise<IBusinessObject> {
        var self = this

        return new Promise<IBusinessObject>((resolve, error) => {

            _.forEach(self.drWB_GLTransaction.WB_GLTransactionItemRows, (aTxnItemRow: WB_GLTransactionItemRow) => {
                if (aTxnItemRow.isActive && aTxnItemRow.cif.WB_GLTXN_ITEM_ID == aTxnItemId) {
                    aTxnItemRow.markDeleted()
                }
            })

            resolve(self)
        })
    }

//</editor-fold>

//</editor-fold>

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {

        let opts = boLoadOptions || {}
        let dbContext: PAdbContext

        if ('dbContext' in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }
        const wrksp_options: CreateOptions = {
            standardConditions: {
                [esWB_GLTransaction]: {DELETED_IND: {neq: '1'}},
                [esWB_GLTransaction_Item]: {DELETED_IND: {neq: '1'}}
            },
            dctIncludeBelongsToNamedRelations: {}
        }

        const aRecResolver = new ESRecordResolver(dbContext, wrksp_options)

        return aRecResolver;

    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {

        return new Promise<void>((resolve, error) => {
            recordResolver.findAllWithRelationships(esWB_GLTransaction, ids).then((result) => {
                resolve()
            }).catch((err) => {
                error(err)
            })
        })
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }


};
PAEnvironment.registerBusinessObjectClass(GLTransactionBO, "GLTransactionBO", "GL Transaction")